import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { catchError, filter, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ChangeDetectorRef } from '@angular/core';

import {
  getCompanyDetails,
  getCompanyKioskSettings,
  login,
  logout,
} from 'app/store/auth/auth.action';
import { AuthTypes } from 'app/store/auth/auth.type';
import { MessageDialogComponent } from 'app/shared/components/message-dialog/message-dialog.component';
import { MessageEnum } from 'app/utils/enums/message.enum';
import { Observable, of } from 'rxjs';
import {
  selectCompanyDetails,
  selectCompanyKioskSettings,
} from 'app/store/auth/auth.selector';
import { ForgotIdPinDialogComponent } from './forgot-id-pin-dialog/forgot-id-pin-dialog.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: false,
})
export class LoginComponent {
  authForm!: FormGroup;
  activeField = 'kiosk_id';
  isLoading = false;
  companyDetails$!: Observable<any>;
  companyID!: any;
  accessType = 'Kiosk';
  isTimesheetEnabled: boolean = true;

  constructor(
    private store: Store,
    private formBuilder: FormBuilder,
    private actions: Actions,
    private router: Router,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.setForm();
    this.companyID = this.route.snapshot.paramMap.get('companyID');
    this.store.dispatch(getCompanyKioskSettings(this.companyID));
    this.store.dispatch(getCompanyDetails(this.companyID));
    this.store
      .select(selectCompanyKioskSettings)
      .pipe(filter((data) => !!data))
      .subscribe((data) => {
        this.accessType = data.access_type;
        this.isTimesheetEnabled = data.isTimesheetEnabled;
        if (data.access_type === 'In App') {
          localStorage.clear();
          sessionStorage.clear();
        }
      });
    this.companyDetails$ = this.store.select(selectCompanyDetails).pipe(
      catchError((err) => {
        return of([]);
      }),
    );
  }

  setForm(): void {
    this.authForm = this.formBuilder.group({
      kiosk_id: ['', [Validators.required]],
      kiosk_pin: ['', [Validators.required]],
    });
  }

  onHandleSubmit(): void {
    if (this.authForm.invalid) return;
    this.isLoading = true;
    const { kiosk_id, kiosk_pin } = this.authForm.value;
    const payload = {
      kiosk_id,
      kiosk_pin,
      companyID: this.companyID,
    };
    this.store.dispatch(login(payload));

    this.actions
      .pipe(
        ofType(AuthTypes.POST_AUTH_LOGIN_CANCEL),
        tap(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe();

    this.actions
      .pipe(
        ofType(AuthTypes.POST_AUTH_LOGIN_SUCCESS),
        tap(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
          this.router.navigate([`${this.companyID}/dashboard`]);
        }),
      )
      .subscribe();
  }

  onHandleKeyPress(key: string) {
    const val = this.authForm.get(this.activeField)?.value;
    this.authForm.get(this.activeField)?.setValue(`${val}${key}`);
  }

  onHandleOk() {
    if (this.authForm.valid && this.activeField === 'kiosk_pin') {
      this.onHandleSubmit();
    } else {
      this.activeField = 'kiosk_pin';
    }
  }

  onHandleClear() {
    this.authForm.get(this.activeField)?.setValue('');
  }

  onHandleForgot(type: string) {
    this.dialog.open(ForgotIdPinDialogComponent, {
      data: { type },
    });
  }

  onHandleBack() {
    this.activeField = 'kiosk_id';
  }
}
